import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown,
  BarChart3, 
  ArrowRight, 
  Zap, 
  Repeat, 
  Layers, 
  Clock, 
  DollarSign, 
  ChevronRight, 
  Globe, 
  Star, 
  ArrowUpRight, 
  ArrowDownRight,
  ArrowDownLeft,
  Bitcoin,
  LineChart,
  PieChart,
  Activity,
  Newspaper,
  ExternalLink,
  ChevronLeft,
  Package
} from 'lucide-react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { MarketData, NewsItem, PortfolioSnapshot, DatabaseUserAsset } from '../hooks/useDatabase';
import { FuturesPosition, Transaction } from '../App';
import { useDatabase } from '../hooks/useDatabase';
import { useMarketData } from '../contexts/MarketDataContext';
import GiveawayCampaignPopup from '../components/GiveawayCampaignPopup';
import GiveawayWinnerPopup from '../components/GiveawayWinnerPopup';
import GiveawayComingSoonPopup from '../components/GiveawayComingSoonPopup';
import { Editorial, EditorialInverted, Typography, BorderRadius } from '../styles/primitives';
import { DASHBOARD_PATHS } from '../routes/dashboardRoutes';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface HomePageProps {
  usdtBalance: number;
  btcBalance: number;
  futuresPositions: FuturesPosition[];
  transactions: Transaction[];
  newsItems: NewsItem[];
  totalPortfolioValue?: number;
  userAssets?: DatabaseUserAsset[];
}

const HomePage: React.FC<HomePageProps> = ({
  usdtBalance,
  btcBalance,
  futuresPositions,
  transactions,
  newsItems,
  totalPortfolioValue = 0,
  userAssets = []
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fetchPortfolioSnapshots, portfolioSnapshots, createPortfolioSnapshot, coingeckoMarketCapData } = useDatabase();
  const { marketData, isConnected: isRealtimeConnected, getPriceBySymbol } = useMarketData();
  const [activeTab, setActiveTab] = useState<'positions' | 'transactions'>('positions');
  const [filteredSnapshots, setFilteredSnapshots] = useState<PortfolioSnapshot[]>([]);
  const [showCampaignPopup, setShowCampaignPopup] = useState(false);
  const [showWinnerPopup, setShowWinnerPopup] = useState(false);
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);

  // Helper function to format date safely
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };
  
  // News pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  
  // Chart period state
  const [chartPeriod, setChartPeriod] = useState<'1W' | '1M' | '3M' | '1Y' | 'All'>('1M');
  
  // Get current BTC price from realtime data
  const currentBtcPrice = getPriceBySymbol('BTCUSDT') || 0;
  
  // Calculate actual portfolio value if not provided or is zero
  const actualPortfolioValue = totalPortfolioValue > 0 ? totalPortfolioValue : (usdtBalance + (btcBalance * currentBtcPrice));
  
  // Check if data is still loading
  const isDataLoading = actualPortfolioValue === 0 && (usdtBalance === 0 && btcBalance === 0 && currentBtcPrice === 0);
  
  // Calculate total PnL from futures positions
  const totalPositionsPnl = futuresPositions.reduce((sum, position) => {
    // Calculate correct unrealized PnL without extra leverage multiplication
    const entryPrice = position.entryPrice || position.entry_price || 0;
    
    // Get live price from market data context first, then fallback to position data
    let currentPrice = 0;
    const livePrice = getPriceBySymbol(position.symbol);
    if (livePrice > 0) {
      currentPrice = livePrice;
    } else {
      currentPrice = position.currentPrice || position.current_price || 0;
    }
    
    const amount = position.amount || 0;
    const side = position.side || 'long';
    // Calculate unrealized PnL based on live price (WITHOUT leverage multiplier)
    let unrealizedPnl = 0;
    if (position.side === 'long') {
      unrealizedPnl = (livePrice - entryPrice) * amount;
    } else {
      unrealizedPnl = (entryPrice - livePrice) * amount;
    }
    
    return sum + unrealizedPnl;
  }, 0);

  // Fetch portfolio snapshots based on selected period
  useEffect(() => {
    const fetchSnapshotsForPeriod = async () => {
      let days = 30; // Default to 1 month
      
      switch (chartPeriod) {
        case '1W':
          days = 7;
          break;
        case '1M':
          days = 30;
          break;
        case '3M':
          days = 90;
          break;
        case '1Y':
          days = 365;
          break;
        case 'All':
          days = 9999; // Fetch all data
          break;
      }
      
      await fetchPortfolioSnapshots(days);
    };
    
    fetchSnapshotsForPeriod();
  }, [chartPeriod, fetchPortfolioSnapshots]);

  // Filter and format portfolio snapshots for the chart
  useEffect(() => {
    if (portfolioSnapshots.length === 0) {
      // If no snapshots, create a default one with current values
      const today = new Date();
      const defaultSnapshots = [{
        id: 'default',
        user_id: '',
        snapshot_date: today.toISOString().split('T')[0],
        total_value: totalPortfolioValue,
        usdt_balance: usdtBalance,
        btc_balance: btcBalance,
        btc_price: currentBtcPrice,
        created_at: today.toISOString()
      }];
      
      setFilteredSnapshots(defaultSnapshots);
      return;
    }
    
    // Filter snapshots based on selected period
    let filteredData = [...portfolioSnapshots];
    
    // Sort by date
    filteredData.sort((a, b) => 
      new Date(a.snapshot_date).getTime() - new Date(b.snapshot_date).getTime()
    );
    
    setFilteredSnapshots(filteredData);
  }, [portfolioSnapshots, totalPortfolioValue, usdtBalance, btcBalance, currentBtcPrice]);
  
  // Get top performing assets
  const getTopPerformingAssets = () => {
    return marketData
      .filter(data => data.price > 0 && data.price !== 104325.3716)
      .sort((a, b) => (b.change_24h ?? 0) - (a.change_24h ?? 0))
      .slice(0, 5);
  };
  
  // Get recent transactions
  const getRecentTransactions = () => {
    return transactions.slice(0, 5);
  };

  // Pagination logic for news
  const indexOfLastNews = currentPage * itemsPerPage;
  const indexOfFirstNews = indexOfLastNews - itemsPerPage;
  const currentNews = newsItems.slice(indexOfFirstNews, indexOfLastNews);
  const totalPages = Math.ceil(newsItems.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  // Calculate high, low, and change values for the selected period
  const calculateStats = () => {
    if (filteredSnapshots.length === 0) {
      return {
        high: actualPortfolioValue,
        low: actualPortfolioValue,
        change: 0,
        changePercentage: 0
      };
    }
    
    const values = filteredSnapshots.map(snapshot => snapshot.total_value);
    const high = Math.max(...values);
    const low = Math.min(...values);
    
    // Calculate change from first to last snapshot
    const firstValue = filteredSnapshots[0].total_value;
    const lastValue = filteredSnapshots[filteredSnapshots.length - 1].total_value;
    const change = lastValue - firstValue;
    const changePercentage = firstValue > 0 ? (change / firstValue) * 100 : 0;
    
    return { high, low, change, changePercentage };
  };
  
  const { high, low, change, changePercentage } = calculateStats();

  // Prepare chart data
  const chartData = {
    labels: filteredSnapshots.map(snapshot => {
      const date = new Date(snapshot.snapshot_date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Portfolio Value',
        data: filteredSnapshots.map(snapshot => snapshot.total_value),
        borderColor: '#10f470',
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 320);
          gradient.addColorStop(0, 'rgba(16, 244, 112, 0.25)');
          gradient.addColorStop(1, 'rgba(16, 244, 112, 0.0)');
          return gradient;
        },
        borderWidth: 2.5,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: '#10f470',
        pointHoverBorderColor: 'rgb(15, 23, 42)',
        pointHoverBorderWidth: 3,
        tension: 0.4,
        fill: true
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(23, 23, 23, 0.95)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(16, 244, 112, 0.6)',
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        titleFont: {
          size: 13,
          weight: 'bold'
        },
        bodyFont: {
          size: 14,
          weight: 'bold'
        },
        callbacks: {
          label: function(context: any) {
            return `$${context.raw.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#000000',
          maxTicksLimit: 7,
          font: {
            size: 11
          }
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.08)',
          drawBorder: false
        },
        ticks: {
          color: '#000000',
          font: {
            size: 11
          },
          callback: function(value: any) {
            return `$${value.toLocaleString('en-US', { notation: 'compact', compactDisplay: 'short' })}`;
          }
        }
      }
    }
  };

  // Get period label for stats
  const getPeriodLabel = () => {
    switch (chartPeriod) {
      case '1W': return '1W';
      case '1M': return '1M';
      case '3M': return '3M';
      case '1Y': return '1Y';
      case 'All': return 'All Time';
      default: return '';
    }
  };

  // Sort market data by market cap for Market Overview section
  const getSortedMarketData = () => {
    // Create a map of market caps from coingeckoMarketCapData
    const marketCapMap = new Map();
    
    if (coingeckoMarketCapData && coingeckoMarketCapData.length > 0) {
      coingeckoMarketCapData.forEach(coin => {
        marketCapMap.set(coin.symbol.toUpperCase(), coin.market_cap);
      });
    }
    
    // Filter valid market data
    const validMarketData = marketData.filter(data => 
      data.price > 0 && 
      data.price !== 104325.3716 && // Filter out placeholder price
      data.symbol.endsWith('USDT') // Only include USDT pairs
    );
    
    // Sort by market cap (descending)
    return validMarketData.sort((a, b) => {
      // Extract base symbol (remove USDT)
      const aSymbol = a.symbol.replace('USDT', '').toLowerCase();
      const bSymbol = b.symbol.replace('USDT', '').toLowerCase();
      
      // Get market caps from map
      const aMarketCap = marketCapMap.get(aSymbol.toUpperCase()) || 0;
      const bMarketCap = marketCapMap.get(bSymbol.toUpperCase()) || 0;
      
      // Sort by market cap (descending)
      return bMarketCap - aMarketCap;
    }).slice(0, 5); // Take top 5 for display
  };

  return (
    <div className="p-8 bg-slate-50/95">
      {/* Giveaway Popups */}
      <GiveawayCampaignPopup forceShow={showCampaignPopup} />
      <GiveawayWinnerPopup forceShow={showWinnerPopup} />
      <GiveawayComingSoonPopup forceShow={showComingSoonPopup} />

      {/* Welcome Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <h1
            className="mb-2"
            style={{
              fontSize: Typography.DisplayMedium,
              fontWeight: Typography.WeightBold,
              color: '#1e293b'
            }}
          >
            {t('home.welcomeTitle')}
          </h1>
        </div>
        <p style={{ color: '#000000', fontSize: Typography.BodyMedium }}>
          {t('home.welcomeSubtitle')}
        </p>
      </div>
      
      {/* Market Overview - Redesigned */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2
            style={{
              fontSize: Typography.HeadlineLarge,
              fontWeight: Typography.WeightBold,
              color: '#000000',
              letterSpacing: '-0.02em'
            }}
          >
            Market Overview
          </h2>
          <div
            className="px-4 py-2"
            style={{
              background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.1), rgba(217, 18, 34, 0.05))',
              border: '1px solid rgba(217, 18, 34, 0.3)',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: Typography.WeightMedium,
              color: '#d91222'
            }}
          >
            Live
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {getSortedMarketData().map((data, index) => {
            const isPositive = (data.change_24h ?? 0) >= 0;
            const changeColor = isPositive ? '#16a34a' : '#dc2626';
            const symbolName = data.symbol.replace('USDT', '');

            return (
              <div
                key={data.symbol}
                className="group relative cursor-pointer overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                  borderRadius: '20px',
                  padding: '20px',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)';
                }}
              >
                {/* Header with symbol and rank */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    {/* Crypto icon placeholder */}
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${isPositive ? 'rgba(22, 163, 74, 0.15)' : 'rgba(220, 38, 38, 0.15)'}, ${isPositive ? 'rgba(22, 163, 74, 0.05)' : 'rgba(220, 38, 38, 0.05)'})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `2px solid ${isPositive ? 'rgba(22, 163, 74, 0.2)' : 'rgba(220, 38, 38, 0.2)'}`,
                        transition: 'all 0.3s ease'
                      }}
                      className="group-hover:scale-110"
                    >
                      {symbolName === 'BTC' ? <Bitcoin size={18} style={{ color: changeColor }} /> :
                       symbolName === 'ETH' ? <Activity size={18} style={{ color: changeColor }} /> :
                       <TrendingUp size={18} style={{ color: changeColor }} />}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: Typography.WeightBold,
                          color: '#000000',
                          letterSpacing: '-0.02em'
                        }}
                      >
                        {symbolName}
                      </div>
                      <div
                        style={{
                          fontSize: '11px',
                          fontWeight: Typography.WeightMedium,
                          color: 'rgba(0, 0, 0, 0.4)',
                          letterSpacing: '0.02em'
                        }}
                      >
                        /USDT
                      </div>
                    </div>
                  </div>

                  {/* Market rank badge */}
                  <div
                    style={{
                      padding: '4px 8px',
                      borderRadius: '8px',
                      background: 'rgba(0, 0, 0, 0.04)',
                      fontSize: '10px',
                      fontWeight: Typography.WeightBold,
                      color: 'rgba(0, 0, 0, 0.5)',
                      letterSpacing: '0.05em'
                    }}
                  >
                    #{index + 1}
                  </div>
                </div>

                {/* Price section */}
                <div className="mb-3">
                  <div
                    className="font-mono"
                    style={{
                      fontSize: '22px',
                      fontWeight: Typography.WeightBold,
                      color: '#000000',
                      letterSpacing: '-0.02em',
                      lineHeight: '1.2'
                    }}
                    translate="no"
                  >
                    ${data.price.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: data.price < 1 ? 6 : 2
                    })}
                  </div>
                </div>

                {/* Change indicator with arrow */}
                <div className="flex items-center justify-between">
                  <div
                    className="flex items-center space-x-1.5 font-mono"
                    style={{
                      fontSize: '14px',
                      fontWeight: Typography.WeightBold,
                      color: changeColor
                    }}
                    translate="no"
                  >
                    {isPositive ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                    <span>{isPositive ? '+' : ''}{(data.change_24h ?? 0).toFixed(2)}%</span>
                  </div>

                  {/* Mini trend indicator */}
                  <div
                    style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      background: isPositive ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.1)',
                      fontSize: '10px',
                      fontWeight: Typography.WeightBold,
                      color: changeColor,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    24H
                  </div>
                </div>

                {/* Subtle bottom accent */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: `linear-gradient(90deg, transparent, ${changeColor}40, transparent)`,
                    opacity: 0,
                    transition: 'opacity 0.3s ease'
                  }}
                  className="group-hover:opacity-100"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Stats and Portfolio Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Quick Stats - Left Side */}
        <div className="space-y-5">
          {/* Portfolio Value Card */}
          <div
            className="group relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.3s ease'
            }}
          >
            {/* Top gradient accent */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.5), rgba(147, 51, 234, 0.5))',
                opacity: 0.6
              }}
            />

            <div className="flex items-center justify-between mb-4">
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(147, 51, 234, 0.15))',
                  border: '2px solid rgba(59, 130, 246, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                className="group-hover:scale-110"
              >
                <Wallet size={22} style={{ color: '#3b82f6' }} />
              </div>
              <div
                style={{
                  padding: '4px 10px',
                  borderRadius: '8px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  fontSize: '10px',
                  fontWeight: Typography.WeightBold,
                  color: '#3b82f6',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                Portfolio
              </div>
            </div>

            <div className="mb-3">
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: Typography.WeightMedium,
                  color: 'rgba(0, 0, 0, 0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '8px'
                }}
              >
                {getPeriodLabel()} {t('home.change')}
              </div>
              <div
                className="font-mono"
                style={{
                  fontSize: '28px',
                  fontWeight: Typography.WeightBold,
                  color: '#000000',
                  letterSpacing: '-0.02em',
                  lineHeight: '1.2'
                }}
                translate="no"
              >
                {isDataLoading ? (
                  <div className="flex items-center gap-2">
                    <div
                      className="w-5 h-5 border-2 animate-spin"
                      style={{
                        borderColor: 'rgba(0, 0, 0, 0.1)',
                        borderTopColor: '#000000',
                        borderRadius: '50%'
                      }}
                    ></div>
                    <span style={{ fontSize: '14px' }}>Loading...</span>
                  </div>
                ) : (
                  `$${actualPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                )}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}
            >
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: Typography.WeightMedium,
                  color: 'rgba(0, 0, 0, 0.6)'
                }}
              >
                {getPeriodLabel()} Change
              </span>
              <div
                className="flex items-center space-x-1 font-mono"
                style={{
                  fontSize: '14px',
                  fontWeight: Typography.WeightBold,
                  color: changePercentage >= 0 ? '#16a34a' : '#dc2626'
                }}
                translate="no"
              >
                {!isDataLoading && (changePercentage >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />)}
                <span>
                  {isDataLoading ? '--' : `${changePercentage >= 0 ? '+' : ''}${changePercentage.toFixed(2)}%`}
                </span>
              </div>
            </div>
          </div>

          {/* Positions P&L Card */}
          <div
            className="group relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.3s ease'
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: totalPositionsPnl >= 0
                  ? 'linear-gradient(90deg, rgba(22, 163, 74, 0.5), rgba(34, 197, 94, 0.5))'
                  : 'linear-gradient(90deg, rgba(220, 38, 38, 0.5), rgba(239, 68, 68, 0.5))',
                opacity: 0.6
              }}
            />

            <div className="flex items-center justify-between mb-4">
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: totalPositionsPnl >= 0
                    ? 'linear-gradient(135deg, rgba(22, 163, 74, 0.15), rgba(34, 197, 94, 0.15))'
                    : 'linear-gradient(135deg, rgba(220, 38, 38, 0.15), rgba(239, 68, 68, 0.15))',
                  border: `2px solid ${totalPositionsPnl >= 0 ? 'rgba(22, 163, 74, 0.2)' : 'rgba(220, 38, 38, 0.2)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                className="group-hover:scale-110"
              >
                {totalPositionsPnl >= 0 ?
                  <TrendingUp size={22} style={{ color: '#16a34a' }} /> :
                  <TrendingDown size={22} style={{ color: '#dc2626' }} />
                }
              </div>
              <div
                style={{
                  padding: '4px 10px',
                  borderRadius: '8px',
                  background: totalPositionsPnl >= 0 ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.1)',
                  fontSize: '10px',
                  fontWeight: Typography.WeightBold,
                  color: totalPositionsPnl >= 0 ? '#16a34a' : '#dc2626',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                P&L
              </div>
            </div>

            <div className="mb-3">
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: Typography.WeightMedium,
                  color: 'rgba(0, 0, 0, 0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '8px'
                }}
              >
                Positions P&L
              </div>
              <div
                className="font-mono"
                style={{
                  fontSize: '28px',
                  fontWeight: Typography.WeightBold,
                  color: totalPositionsPnl >= 0 ? '#16a34a' : '#dc2626',
                  letterSpacing: '-0.02em',
                  lineHeight: '1.2'
                }}
                translate="no"
              >
                {totalPositionsPnl >= 0 ? '+' : ''}{totalPositionsPnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}
            >
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: Typography.WeightMedium,
                  color: 'rgba(0, 0, 0, 0.6)'
                }}
              >
                Open Positions
              </span>
              <div
                className="flex items-center space-x-2"
              >
                <div
                  style={{
                    padding: '4px 12px',
                    borderRadius: '8px',
                    background: 'rgba(0, 0, 0, 0.06)',
                    fontSize: '14px',
                    fontWeight: Typography.WeightBold,
                    color: '#000000'
                  }}
                  translate="no"
                >
                  {futuresPositions.length}
                </div>
              </div>
            </div>
          </div>

          {/* BTC Price Card */}
          <div
            className="group relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #fff7ed 100%)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.3s ease'
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, rgba(249, 115, 22, 0.5), rgba(251, 146, 60, 0.5))',
                opacity: 0.6
              }}
            />

            <div className="flex items-center justify-between mb-4">
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.15), rgba(251, 146, 60, 0.15))',
                  border: '2px solid rgba(249, 115, 22, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                className="group-hover:scale-110"
              >
                <Bitcoin size={22} style={{ color: '#f97316' }} />
              </div>
              <div
                style={{
                  padding: '4px 10px',
                  borderRadius: '8px',
                  background: 'rgba(249, 115, 22, 0.1)',
                  fontSize: '10px',
                  fontWeight: Typography.WeightBold,
                  color: '#f97316',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                BTC
              </div>
            </div>

            <div className="mb-3">
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: Typography.WeightMedium,
                  color: 'rgba(0, 0, 0, 0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '8px'
                }}
              >
                BTC Price
              </div>
              <div
                className="font-mono"
                style={{
                  fontSize: '28px',
                  fontWeight: Typography.WeightBold,
                  color: '#000000',
                  letterSpacing: '-0.02em',
                  lineHeight: '1.2'
                }}
                translate="no"
              >
                ${currentBtcPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}
            >
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: Typography.WeightMedium,
                  color: 'rgba(0, 0, 0, 0.6)'
                }}
              >
                24h Change
              </span>
              <div
                className="flex items-center space-x-1 font-mono"
                style={{
                  fontSize: '14px',
                  fontWeight: Typography.WeightBold,
                  color: (marketData.find(d => d.symbol === 'BTCUSDT')?.change_24h ?? 0) >= 0 ? '#16a34a' : '#dc2626'
                }}
                translate="no"
              >
                {(marketData.find(d => d.symbol === 'BTCUSDT')?.change_24h ?? 0) >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                <span>
                  {(marketData.find(d => d.symbol === 'BTCUSDT')?.change_24h ?? 0) >= 0 ? '+' : ''}
                  {(marketData.find(d => d.symbol === 'BTCUSDT')?.change_24h ?? 0).toFixed(2)}%
                </span>
              </div>
            </div>
          </div>

          {/* Trading Volume Card */}
          <div
            className="group relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.3s ease'
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, rgba(168, 85, 247, 0.5), rgba(192, 132, 252, 0.5))',
                opacity: 0.6
              }}
            />

            <div className="flex items-center justify-between mb-4">
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(192, 132, 252, 0.15))',
                  border: '2px solid rgba(168, 85, 247, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                className="group-hover:scale-110"
              >
                <BarChart3 size={22} style={{ color: '#a855f7' }} />
              </div>
              <div
                style={{
                  padding: '4px 10px',
                  borderRadius: '8px',
                  background: 'rgba(168, 85, 247, 0.1)',
                  fontSize: '10px',
                  fontWeight: Typography.WeightBold,
                  color: '#a855f7',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                Volume
              </div>
            </div>

            <div className="mb-3">
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: Typography.WeightMedium,
                  color: 'rgba(0, 0, 0, 0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '8px'
                }}
              >
                Trading Volume
              </div>
              <div
                className="font-mono"
                style={{
                  fontSize: '28px',
                  fontWeight: Typography.WeightBold,
                  color: '#000000',
                  letterSpacing: '-0.02em',
                  lineHeight: '1.2'
                }}
                translate="no"
              >
                $125,430.50
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}
            >
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: Typography.WeightMedium,
                  color: 'rgba(0, 0, 0, 0.6)'
                }}
              >
                {getPeriodLabel()} Change
              </span>
              <div
                className="flex items-center space-x-1 font-mono"
                style={{
                  fontSize: '14px',
                  fontWeight: Typography.WeightBold,
                  color: '#16a34a'
                }}
                translate="no"
              >
                <ArrowUpRight size={16} />
                <span>+12.5%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Portfolio Performance - Right Side */}
        <div
          className="lg:col-span-2 p-6 shadow-lg"
          style={{
            background: '#ffffff',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2
              style={{
                fontSize: Typography.HeadlineSmall,
                fontWeight: Typography.WeightBold,
                color: '#1e293b'
              }}
            >
              Portfolio Performance
            </h2>
            <div className="flex gap-2">
              {(['1W', '1M', '3M', '1Y', 'All'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setChartPeriod(period)}
                  className="px-3 py-1 transition-all"
                  style={{
                    fontSize: Typography.BodySmall,
                    color: chartPeriod === period ? '#d91222' : '#000000',
                    backgroundColor: 'transparent',
                    borderBottom: chartPeriod === period ? '2px solid #d91222' : '2px solid transparent',
                    borderRadius: BorderRadius.None,
                    fontWeight: chartPeriod === period ? Typography.WeightMedium : Typography.WeightRegular
                  }}
                  onMouseEnter={(e) => {
                    if (chartPeriod !== period) {
                      e.currentTarget.style.color = '#d91222';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (chartPeriod !== period) {
                      e.currentTarget.style.color = '#000000';
                    }
                  }}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>

          <div className="h-80 mb-4">
            <Line data={chartData} options={chartOptions} />
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6" style={{ fontSize: Typography.BodySmall }}>
            <div>
              <div style={{ color: '#000000' }}>High ({getPeriodLabel()})</div>
              <div
                className="font-mono"
                style={{ color: '#000000', fontWeight: Typography.WeightSemibold }}
                translate="no"
              >
                ${high.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <div style={{ color: '#000000' }}>Low ({getPeriodLabel()})</div>
              <div
                className="font-mono"
                style={{ color: '#000000', fontWeight: Typography.WeightSemibold }}
                translate="no"
              >
                ${low.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <div style={{ color: '#000000' }}>Change ({getPeriodLabel()})</div>
              <div
                className="font-mono"
                style={{
                  fontWeight: Typography.WeightSemibold,
                  color: change >= 0 ? '#22c55e' : '#ef4444'
                }}
                translate="no"
              >
                {change >= 0 ? '+' : ''}${change.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* Portfolio Breakdown Section */}
          <div
            style={{
              borderTop: '1px solid rgba(0, 0, 0, 0.06)',
              paddingTop: '20px'
            }}
          >
            <div
              style={{
                fontSize: '13px',
                fontWeight: Typography.WeightBold,
                color: 'rgba(0, 0, 0, 0.7)',
                marginBottom: '16px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              Portfolio Breakdown
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Asset Allocation */}
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(16, 185, 129, 0.05))',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  borderRadius: '12px',
                  padding: '16px'
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(16, 185, 129, 0.15))',
                      border: '2px solid rgba(59, 130, 246, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <PieChart size={16} style={{ color: '#3b82f6' }} />
                  </div>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: Typography.WeightBold,
                      color: 'rgba(0, 0, 0, 0.7)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    Assets
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#3b82f6'
                        }}
                      />
                      <span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>USDT</span>
                    </div>
                    <span
                      className="font-mono"
                      style={{
                        fontSize: '12px',
                        fontWeight: Typography.WeightBold,
                        color: '#000000'
                      }}
                      translate="no"
                    >
                      {actualPortfolioValue > 0
                        ? ((usdtBalance / actualPortfolioValue) * 100).toFixed(1)
                        : '0.0'}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#f59e0b'
                        }}
                      />
                      <span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>BTC</span>
                    </div>
                    <span
                      className="font-mono"
                      style={{
                        fontSize: '12px',
                        fontWeight: Typography.WeightBold,
                        color: '#000000'
                      }}
                      translate="no"
                    >
                      {actualPortfolioValue > 0
                        ? (((btcBalance * currentBtcPrice) / actualPortfolioValue) * 100).toFixed(1)
                        : '0.0'}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Positions P&L */}
              <div
                style={{
                  background: totalPositionsPnl >= 0
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.05), rgba(22, 163, 74, 0.05))'
                    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.05), rgba(220, 38, 38, 0.05))',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  borderRadius: '12px',
                  padding: '16px'
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: totalPositionsPnl >= 0
                        ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.15))'
                        : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15))',
                      border: `2px solid ${totalPositionsPnl >= 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {totalPositionsPnl >= 0 ? (
                      <TrendingUp size={16} style={{ color: '#16a34a' }} />
                    ) : (
                      <TrendingDown size={16} style={{ color: '#dc2626' }} />
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: Typography.WeightBold,
                      color: 'rgba(0, 0, 0, 0.7)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    Unrealized P&L
                  </span>
                </div>

                <div className="space-y-2">
                  <div
                    className="font-mono"
                    style={{
                      fontSize: '18px',
                      fontWeight: Typography.WeightBold,
                      color: totalPositionsPnl >= 0 ? '#16a34a' : '#dc2626',
                      letterSpacing: '-0.01em'
                    }}
                    translate="no"
                  >
                    {totalPositionsPnl >= 0 ? '+' : ''}{totalPositionsPnl.toFixed(2)} USDT
                  </div>
                  <div
                    style={{
                      fontSize: '11px',
                      color: 'rgba(0, 0, 0, 0.5)'
                    }}
                  >
                    From {futuresPositions.length} open position{futuresPositions.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {/* Performance Metrics */}
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.05), rgba(168, 85, 247, 0.05))',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  borderRadius: '12px',
                  padding: '16px'
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.15), rgba(168, 85, 247, 0.15))',
                      border: '2px solid rgba(147, 51, 234, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <BarChart3 size={16} style={{ color: '#9333ea' }} />
                  </div>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: Typography.WeightBold,
                      color: 'rgba(0, 0, 0, 0.7)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    Performance
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>Return</span>
                    <span
                      className="font-mono"
                      style={{
                        fontSize: '12px',
                        fontWeight: Typography.WeightBold,
                        color: changePercentage >= 0 ? '#16a34a' : '#dc2626'
                      }}
                      translate="no"
                    >
                      {changePercentage >= 0 ? '+' : ''}{changePercentage.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>Range</span>
                    <span
                      className="font-mono"
                      style={{
                        fontSize: '12px',
                        fontWeight: Typography.WeightBold,
                        color: '#000000'
                      }}
                      translate="no"
                    >
                      ${(high - low).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Activity Stats */}
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.05), rgba(249, 115, 22, 0.05))',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  borderRadius: '12px',
                  padding: '16px'
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.15), rgba(249, 115, 22, 0.15))',
                      border: '2px solid rgba(251, 146, 60, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Activity size={16} style={{ color: '#f97316' }} />
                  </div>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: Typography.WeightBold,
                      color: 'rgba(0, 0, 0, 0.7)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    Activity
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>Data Points</span>
                    <span
                      className="font-mono"
                      style={{
                        fontSize: '12px',
                        fontWeight: Typography.WeightBold,
                        color: '#000000'
                      }}
                      translate="no"
                    >
                      {filteredSnapshots.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>Positions</span>
                    <span
                      className="font-mono"
                      style={{
                        fontSize: '12px',
                        fontWeight: Typography.WeightBold,
                        color: '#000000'
                      }}
                      translate="no"
                    >
                      {futuresPositions.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Tabs */}
      <div
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(0, 0, 0, 0.06)',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}
      >
        {/* Top accent bar */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.5), rgba(16, 185, 129, 0.5))',
            opacity: 0.6
          }}
        />

        {/* Tabs */}
        <div className="flex items-center px-6 pt-6 pb-2 gap-2">
          {(['positions', 'transactions'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="relative px-5 py-2.5 capitalize transition-all duration-300"
              style={{
                fontWeight: activeTab === tab ? Typography.WeightBold : Typography.WeightMedium,
                fontSize: '14px',
                color: activeTab === tab ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                background: activeTab === tab
                  ? 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)'
                  : 'transparent',
                borderRadius: '12px',
                letterSpacing: '0.02em',
                boxShadow: activeTab === tab ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab) {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.color = '#000000';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                }
              }}
            >
              {tab}
              {activeTab === tab && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-2px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '40%',
                    height: '2px',
                    background: '#ffffff',
                    borderRadius: '2px'
                  }}
                />
              )}
            </button>
          ))}
        </div>

        <div className="p-6 pt-4">
          {activeTab === 'positions' && (
            <div className="space-y-3">
              {futuresPositions.length > 0 ? (
                futuresPositions.map((position, index) => {
                  // Get live price from market data context first, then fallback to position data
                  let currentPrice = 0;
                  const livePrice = getPriceBySymbol(position.symbol);
                  if (livePrice > 0) {
                    currentPrice = livePrice;
                  } else {
                    currentPrice = position.currentPrice || position.current_price || 0;
                  }

                  const entryPrice = position.entryPrice || position.entry_price || 0;
                  const amount = position.amount || 0;
                  const side = position.side || 'long';

                  // Calculate unrealized PnL based on live price (WITHOUT leverage multiplier)
                  let unrealizedPnl = 0;
                  if (side === 'long') {
                    unrealizedPnl = (currentPrice - entryPrice) * amount;
                  } else {
                    unrealizedPnl = (entryPrice - currentPrice) * amount;
                  }

                  return (
                    <div
                      key={position.id || index}
                      className="group relative overflow-hidden transition-all duration-200"
                      style={{
                        background: '#ffffff',
                        border: '1px solid rgba(0, 0, 0, 0.06)',
                        borderRadius: '16px',
                        padding: '16px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.02)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {/* Side indicator bar */}
                      <div
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          background: side === 'long'
                            ? 'linear-gradient(180deg, #22c55e, #16a34a)'
                            : 'linear-gradient(180deg, #ef4444, #dc2626)',
                          borderTopLeftRadius: '16px',
                          borderBottomLeftRadius: '16px'
                        }}
                      />

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            style={{
                              width: '42px',
                              height: '42px',
                              borderRadius: '12px',
                              background: side === 'long'
                                ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.15))'
                                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15))',
                              border: `2px solid ${side === 'long' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {side === 'long' ? (
                              <TrendingUp size={20} style={{ color: '#16a34a' }} />
                            ) : (
                              <TrendingDown size={20} style={{ color: '#dc2626' }} />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                style={{
                                  color: '#000000',
                                  fontWeight: Typography.WeightBold,
                                  fontSize: '15px'
                                }}
                              >
                                {position.symbol}
                              </span>
                              <div
                                style={{
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  background: side === 'long' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                  fontSize: '10px',
                                  fontWeight: Typography.WeightBold,
                                  color: side === 'long' ? '#16a34a' : '#dc2626',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em'
                                }}
                              >
                                {side}
                              </div>
                              <div
                                style={{
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  background: 'rgba(0, 0, 0, 0.06)',
                                  fontSize: '10px',
                                  fontWeight: Typography.WeightBold,
                                  color: 'rgba(0, 0, 0, 0.7)',
                                  letterSpacing: '0.05em'
                                }}
                              >
                                {position.leverage || 1}x
                              </div>
                            </div>
                            <div
                              className="flex items-center gap-2 font-mono"
                              style={{
                                color: 'rgba(0, 0, 0, 0.5)',
                                fontSize: '12px'
                              }}
                            >
                              <span>${entryPrice.toFixed(2)}</span>
                              <ArrowRight size={12} />
                              <span style={{ color: '#000000', fontWeight: Typography.WeightMedium }}>${currentPrice.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div
                            className="font-mono mb-1"
                            style={{
                              fontWeight: Typography.WeightBold,
                              color: unrealizedPnl >= 0 ? '#16a34a' : '#dc2626',
                              fontSize: '16px',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            {unrealizedPnl >= 0 ? '+' : ''}{unrealizedPnl.toFixed(2)} USDT
                          </div>
                          <div
                            style={{
                              padding: '4px 10px',
                              borderRadius: '8px',
                              background: unrealizedPnl >= 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                              fontSize: '11px',
                              fontWeight: Typography.WeightMedium,
                              color: unrealizedPnl >= 0 ? '#16a34a' : '#dc2626',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            {unrealizedPnl >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                            {((unrealizedPnl / (entryPrice * amount)) * 100).toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12">
                  <div
                    style={{
                      width: '64px',
                      height: '64px',
                      margin: '0 auto 16px',
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))',
                      border: '2px solid rgba(59, 130, 246, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Package size={32} style={{ color: 'rgba(0, 0, 0, 0.4)' }} />
                  </div>
                  <p
                    className="mb-4"
                    style={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontSize: '14px',
                      fontWeight: Typography.WeightMedium
                    }}
                  >
                    {t('home.noPositions')}
                  </p>
                  <button
                    onClick={() => navigate(DASHBOARD_PATHS.futures)}
                    className="transition-all duration-300"
                    style={{
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
                      color: '#ffffff',
                      borderRadius: '12px',
                      fontWeight: Typography.WeightBold,
                      fontSize: '14px',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {t('home.startTrading')}
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="space-y-3">
              {getRecentTransactions().length > 0 ? (
                getRecentTransactions().map((transaction, index) => (
                  <div
                    key={index}
                    className="group transition-all duration-200"
                    style={{
                      background: '#ffffff',
                      border: '1px solid rgba(0, 0, 0, 0.06)',
                      borderRadius: '16px',
                      padding: '16px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.02)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          style={{
                            width: '42px',
                            height: '42px',
                            borderRadius: '12px',
                            background: transaction.amount > 0
                              ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.15))'
                              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15))',
                            border: `2px solid ${transaction.amount > 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {transaction.amount > 0 ? (
                            <ArrowDownRight size={20} style={{ color: '#16a34a' }} />
                          ) : (
                            <ArrowUpRight size={20} style={{ color: '#dc2626' }} />
                          )}
                        </div>
                        <div>
                          <div
                            style={{
                              color: '#000000',
                              fontWeight: Typography.WeightBold,
                              fontSize: '14px',
                              marginBottom: '4px'
                            }}
                          >
                            {transaction.description}
                          </div>
                          <div
                            className="flex items-center gap-2"
                            style={{
                              color: 'rgba(0, 0, 0, 0.5)',
                              fontSize: '12px'
                            }}
                          >
                            <Clock size={12} />
                            <span>{formatDate(transaction.created_at || transaction.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                      <div
                        className="font-mono text-right"
                      >
                        <div
                          style={{
                            fontWeight: Typography.WeightBold,
                            color: transaction.amount > 0 ? '#16a34a' : '#dc2626',
                            fontSize: '16px',
                            letterSpacing: '-0.01em',
                            marginBottom: '4px'
                          }}
                        >
                          {transaction.amount > 0 ? '+' : ''}{transaction.amount.toFixed(2)} USDT
                        </div>
                        <div
                          style={{
                            padding: '3px 10px',
                            borderRadius: '8px',
                            background: transaction.amount > 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            fontSize: '10px',
                            fontWeight: Typography.WeightBold,
                            color: transaction.amount > 0 ? '#16a34a' : '#dc2626',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            display: 'inline-block'
                          }}
                        >
                          {transaction.amount > 0 ? 'Credit' : 'Debit'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <div
                    style={{
                      width: '64px',
                      height: '64px',
                      margin: '0 auto 16px',
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))',
                      border: '2px solid rgba(59, 130, 246, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Package size={32} style={{ color: 'rgba(0, 0, 0, 0.4)' }} />
                  </div>
                  <p
                    className="mb-4"
                    style={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontSize: '14px',
                      fontWeight: Typography.WeightMedium
                    }}
                  >
                    {t('home.noTransactions')}
                  </p>
                  <button
                    onClick={() => navigate(DASHBOARD_PATHS.wallet)}
                    className="transition-all duration-300"
                    style={{
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
                      color: '#ffffff',
                      borderRadius: '12px',
                      fontWeight: Typography.WeightBold,
                      fontSize: '14px',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {t('home.goToWallet')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* News Section */}
      <div
        className="mt-8 p-6 shadow-lg"
        style={{
          background: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 flex items-center justify-center"
              style={{
                backgroundColor: 'rgba(16, 244, 112, 0.15)',
                borderRadius: '8px',
                border: '1px solid rgba(16, 244, 112, 0.4)',
                boxShadow: '0 0 10px rgba(16, 244, 112, 0.2)'
              }}
            >
              <Newspaper size={20} style={{ color: '#10f470' }} />
            </div>
            <h2
              style={{
                fontSize: Typography.HeadlineSmall,
                fontWeight: Typography.WeightSemibold,
                color: '#1e293b'
              }}
            >
              {t('home.marketNews')}
            </h2>
          </div>
          <div
            className="px-3 py-1 border"
            style={{
              fontSize: Typography.Caption,
              color: '#000000',
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              borderColor: 'rgba(0, 0, 0, 0.08)',
              borderRadius: '8px'
            }}
          >
            {newsItems.length} articles
          </div>
        </div>
        
        <div className="space-y-6">
          {currentNews.length > 0 ? (
            currentNews.map((news, index) => (
              <div
                key={news.id || index}
                className="p-6 border transition-all duration-300"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  borderColor: 'rgba(0, 0, 0, 0.08)',
                  borderRadius: '12px'
                }}
              >
                <div className="flex gap-6">
                  {news.image_url && (
                    <div
                      className="w-24 h-24 overflow-hidden flex-shrink-0"
                      style={{ borderRadius: '8px' }}
                    >
                      <img
                        src={news.image_url}
                        alt={news.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        style={{
                          fontSize: Typography.Caption,
                          fontWeight: Typography.WeightMedium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: Editorial.Red
                        }}
                      >
                        {news.category}
                      </span>
                      <span style={{ fontSize: Typography.Caption, color: '#000000' }}>
                        {news.source}
                      </span>
                      <span style={{ fontSize: Typography.Caption, color: '#000000' }}>
                        •
                      </span>
                      <span style={{ fontSize: Typography.Caption, color: '#000000' }}>
                        {new Date(news.published_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h3
                      className="mb-3 leading-relaxed"
                      style={{
                        fontWeight: Typography.WeightSemibold,
                        color: '#000000',
                        fontSize: Typography.BodyLarge
                      }}
                    >
                      {news.title}
                    </h3>
                    <p
                      className="line-clamp-3 mb-4 leading-relaxed"
                      style={{
                        color: '#000000',
                        fontSize: Typography.BodySmall
                      }}
                    >
                      {news.summary}
                    </p>
                    <a
                      href={news.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 transition-colors px-4 py-2"
                      style={{
                        color: '#ffffff',
                        backgroundColor: '#d91222',
                        fontSize: Typography.BodySmall,
                        fontWeight: Typography.WeightMedium,
                        borderRadius: '6px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#b81018';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#d91222';
                      }}
                    >
                      {t('home.readMore')}
                      <ExternalLink size={14} style={{ color: '#ffffff' }} />
                    </a>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Newspaper
                size={64}
                className="mx-auto mb-6"
                style={{ color: '#000000' }}
              />
              <p
                className="mb-2"
                style={{ fontSize: Typography.BodyLarge, color: '#000000' }}
              >
                {t('home.noNewsAvailable')}
              </p>
              <p style={{ fontSize: Typography.BodySmall, color: '#000000' }}>
                {t('home.checkBackLater')}
              </p>
            </div>
          )}
        </div>

        {/* News Pagination */}
        {totalPages > 1 && (
          <div
            className="flex items-center justify-between mt-8 pt-6"
            style={{ borderTop: `1px solid rgba(0, 0, 0, 0.08)` }}
          >
            <div style={{ fontSize: Typography.BodySmall, color: '#000000' }}>
              {t('home.pageInfo', { current: currentPage, total: totalPages })}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className="p-2 transition-all duration-200"
                style={{
                  color: currentPage === 1 ? '#94a3b8' : '#64748b',
                  borderRadius: '8px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.color = Editorial.Red;
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.color = '#64748b';
                  }
                }}
              >
                <ChevronLeft size={16} />
              </button>
              <span
                className="px-4"
                style={{ fontSize: Typography.BodySmall, color: '#000000' }}
              >
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className="p-2 transition-all duration-200"
                style={{
                  backgroundColor: 'transparent',
                  color: currentPage === totalPages ? '#94a3b8' : '#64748b',
                  borderRadius: '8px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.color = Editorial.Red;
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.color = '#64748b';
                  }
                }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
